import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  SEASONS, CATEGORIES, CATEGORY_MAP, REFLECTIONS, REFLECTION_TYPES, FREE_SEASON,
} from "@/data/categories";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft, ArrowRight, Check } from "@phosphor-icons/react";

const inputCls =
  "bg-[#F4EFE6] border-[#4A4540]/20 focus-visible:ring-[#E07A5A] text-[#2C2A28]";

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
        active
          ? "border-[#E07A5A] bg-[#E07A5A] text-white"
          : "border-[#4A4540]/25 bg-white text-[#4A4540] hover:border-[#E07A5A]"
      }`}
    >
      {children}
    </button>
  );
}

const emptyForm = {
  season: FREE_SEASON,
  title: "",
  category: "",
  subtype: "",
  custom_event: "",
  event_date: "",
  location: "",
  reflection_type: "",
  reflection_items: [],
  description: "",
  feeling: "",
  photo_reminder: "",
};

export function AddMemoryWizard({ open, onOpenChange, entryType = "memory", isPro, lockedSeason = null, onCreated }) {
  const isDream = entryType === "dream";
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const memorySteps = ["Season of Life", "Type & Title", "Date & Location", "Reflections", "Your words", "Photo reminder"];
  const dreamSteps = ["Your dream", "How it feels", "Photo reminder"];
  const steps = isDream ? dreamSteps : memorySteps;

  const seasonLocked = !isDream && !!lockedSeason;
  const startIndex = seasonLocked ? 1 : 0;
  const visibleSteps = steps.slice(startIndex);
  const visibleIndex = step - startIndex;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleItem = (v) =>
    setForm((f) => ({
      ...f,
      reflection_items: f.reflection_items.includes(v)
        ? f.reflection_items.filter((x) => x !== v)
        : [...f.reflection_items, v],
    }));

  const reset = () => {
    setForm({ ...emptyForm, season: seasonLocked ? lockedSeason : FREE_SEASON });
    setStep(startIndex);
  };

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, season: seasonLocked ? lockedSeason : FREE_SEASON });
      setStep(startIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockedSeason, isDream]);

  const canNext = () => {
    if (isDream) return step !== 0 || form.title.trim().length > 0;
    if (step === 0) return !!form.season;
    if (step === 1) return form.title.trim().length > 0 && (!!form.category || !!form.custom_event);
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const isGrowth = form.reflection_type === "Personal Growth";
      const res = await api.post("/events", {
        entry_type: entryType,
        season: form.season,
        title: form.title,
        category: isDream ? null : form.category || null,
        subtype: isDream ? null : form.subtype || null,
        custom_event: isDream ? null : form.custom_event || null,
        event_date: form.event_date,
        location: form.location,
        description: form.description,
        // New two-step reflections + free-text feeling, mapped onto existing
        // fields so insights, keepsakes and cards keep working unchanged.
        reflection_type: form.reflection_type || null,
        reflection_items: form.reflection_items,
        reflections_strengths: isGrowth ? form.reflection_items : [],
        reflections_lessons: !isGrowth ? form.reflection_items : [],
        feeling: form.feeling,
        feelings: form.feeling ? [form.feeling] : [],
        photo_reminder: form.photo_reminder.trim() || null,
      });
      toast.success(isDream ? "Dream saved" : "Memory added to your journey");
      reset();
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "locked_feature") toast.error("This is available with Lifetime Access.");
      else toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const subtypes = CATEGORY_MAP[form.category]?.subtypes || [];
  const reflectionGroups = form.reflection_type ? REFLECTIONS[form.reflection_type] : [];
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        data-testid="add-memory-dialog"
        className="max-h-[90vh] overflow-y-auto bg-[#FDFBF7] border-[#4A4540]/15 sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2C2A28]">
            {isDream ? "Record a dream or goal" : "Add a life memory"}
          </DialogTitle>
        </DialogHeader>

        <div className="sticky -top-6 z-10 -mx-6 border-b border-[#4A4540]/10 bg-[#FDFBF7] px-6 py-3">
        <div data-testid="wizard-nav" className="flex items-center justify-between gap-3">
          <Button variant="outline" data-testid="wizard-back-btn"
            onClick={() => (step === startIndex ? onOpenChange(false) : setStep((s) => s - 1))}
            className="min-h-[44px] rounded-full border-2 border-[#36858B] bg-white px-5 text-base font-semibold text-[#245E63] hover:bg-[#EBF4F5]">
            <ArrowLeft size={18} weight="bold" className="mr-1" /> Back
          </Button>
          {isLast ? (
            <Button data-testid="save-memory-btn" disabled={saving} onClick={submit} className="min-h-[44px] rounded-full bg-[#E06D53] px-6 text-base font-semibold text-white hover:bg-[#C85B43]">
              <Check size={18} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save memory"}
            </Button>
          ) : (
            <Button data-testid="wizard-next-btn" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="min-h-[44px] rounded-full bg-[#E06D53] px-6 text-base font-semibold text-white hover:bg-[#C85B43]">
              Next <ArrowRight size={18} weight="bold" className="ml-1" />
            </Button>
          )}
        </div>
        {!canNext() && (
          <p data-testid="wizard-next-hint" className="mt-2 text-xs text-[#A84A32]">
            {isDream ? "Write your dream to continue." : "Choose a type (or type your own) and add a title to continue."}
          </p>
        )}
        </div>

        <div className="flex items-center gap-1.5">
          {visibleSteps.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= visibleIndex ? "bg-[#E07A5A]" : "bg-[#E4DAC8]"}`} />
          ))}
        </div>
        <p className="text-sm text-[#8A7F76]">Step {visibleIndex + 1} of {visibleSteps.length} · {steps[step]}</p>
        {seasonLocked && (
          <p className="-mt-1 text-xs text-[#7EA193]" data-testid="wizard-season-note">
            Adding to <strong>{lockedSeason}</strong>
          </p>
        )}

        <div className="min-h-[240px] py-1">
          {/* Step 0 — Season of Life */}
          {!isDream && step === 0 && (
            <div className="space-y-3">
              <p className="text-[#4A4540]">Which season of life was this?</p>
              <div className="grid grid-cols-2 gap-3">
                {SEASONS.map((s) => {
                  const locked = !isPro && s.id !== FREE_SEASON;
                  const active = form.season === s.id;
                  return (
                    <button
                      key={s.id} type="button" data-testid={`season-${s.id}`} disabled={locked}
                      onClick={() => set("season", s.id)}
                      className={`relative rounded-lg border p-4 text-left transition-colors ${active ? "border-[#E07A5A] bg-[#F4EFE6]" : "border-[#4A4540]/20 bg-white"} ${locked ? "cursor-not-allowed opacity-55" : "hover:border-[#E07A5A]"}`}
                    >
                      <p className="font-serif text-lg text-[#2C2A28]">{s.label}</p>
                      <p className="text-xs text-[#8A7F76]">{s.note}</p>
                      {locked && <span className="absolute right-3 top-3 text-[#8A7F76]"><Lock size={16} weight="fill" /></span>}
                      {s.free && <span className="mt-1 inline-block rounded-full bg-[#7EA193] px-2 py-0.5 text-[10px] text-white">Free</span>}
                    </button>
                  );
                })}
              </div>
              {!isPro && <p className="text-xs text-[#8A7F76]">Other seasons unlock with Lifetime Access.</p>}
            </div>
          )}

          {/* Step 1 — Type of Memories + Title */}
          {!isDream && step === 1 && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#4A4540]">Type of Memories</Label>
                <Select value={form.category} onValueChange={(v) => { set("category", v); set("subtype", ""); }}>
                  <SelectTrigger data-testid="memory-category-select" className={inputCls}>
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 bg-[#FDFBF7]">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.category && (
                <div>
                  <Label className="text-[#4A4540]">Pick one (optional)</Label>
                  <Select value={form.subtype} onValueChange={set.bind(null, "subtype")}>
                    <SelectTrigger data-testid="memory-subtype-select" className={inputCls}>
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 bg-[#FDFBF7]">
                      {subtypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-[#4A4540]">Or type your own</Label>
                <Input data-testid="memory-custom-input" value={form.custom_event} onChange={(e) => set("custom_event", e.target.value)} placeholder="Something else…" className={inputCls} />
              </div>
              <div>
                <Label className="text-[#4A4540]">Give this memory a title</Label>
                <Input data-testid="memory-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Dad brought me an ice cream" className={inputCls} />
              </div>
            </div>
          )}

          {/* Step 2 — Date & Location */}
          {!isDream && step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#4A4540]">Date</Label>
                <Input data-testid="memory-date-input" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} />
              </div>
              <div>
                <Label className="text-[#4A4540]">Location</Label>
                <Input data-testid="memory-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Where did this happen?" className={inputCls} />
              </div>
            </div>
          )}

          {/* Step 3 — Reflections (two-step) */}
          {!isDream && step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#4A4540]">Reflections</Label>
                <Select value={form.reflection_type} onValueChange={(v) => { set("reflection_type", v); set("reflection_items", []); }}>
                  <SelectTrigger data-testid="reflection-type-select" className={inputCls}>
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FDFBF7]">
                    {REFLECTION_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`reflection-type-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!form.reflection_type && (
                <p className="text-sm text-[#8A7F76]">First choose Personal Growth or Life Challenges.</p>
              )}
              {reflectionGroups.map((g) => (
                <div key={g.group}>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#A84A32]">{g.group}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.items.map((it) => (
                      <Chip key={it} testid={`reflection-item-${it}`} active={form.reflection_items.includes(it)} onClick={() => toggleItem(it)}>{it}</Chip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4 — Your words + feeling */}
          {!isDream && step === 4 && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#4A4540]">Share the memory in your words</Label>
                <Textarea data-testid="memory-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What happened, and what made it matter?" className={inputCls} rows={5} />
              </div>
              <div>
                <Label className="text-[#4A4540]">How does this memory feel?</Label>
                <Input data-testid="memory-feeling-input" value={form.feeling} onChange={(e) => set("feeling", e.target.value)} placeholder="Describe the feeling in your words" className={inputCls} />
              </div>
            </div>
          )}

          {/* Dream steps */}
          {isDream && step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#4A4540]">Your dream or goal</Label>
                <Input data-testid="dream-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Travel to Japan one day" className={inputCls} />
              </div>
              <div>
                <Label className="text-[#4A4540]">A letter to your future self</Label>
                <Textarea data-testid="dream-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Write what you hope for, and why it matters to you…" className={inputCls} rows={5} />
              </div>
            </div>
          )}
          {isDream && step === 1 && (
            <div>
              <Label className="text-[#4A4540]">How does this dream feel?</Label>
              <Input data-testid="dream-feeling-input" value={form.feeling} onChange={(e) => set("feeling", e.target.value)} placeholder="Describe the feeling in your words" className={inputCls} />
            </div>
          )}

          {/* Photo reminder (shared last step) — text only, no upload */}
          {isLast && (
            <div className="space-y-2">
              <Label className="text-[#4A4540]">Photo reminder (optional)</Label>
              <p className="text-xs text-[#8A7F76]">Which photo would you like to use with this later? Text only — photos are never uploaded. You'll choose the actual photo privately on your own device in the Ebook Studio.</p>
              <Input data-testid="wizard-photo-reminder-input" value={form.photo_reminder} onChange={(e) => set("photo_reminder", e.target.value)} placeholder="e.g. The beach photo in Mum's blue album" maxLength={200} className={inputCls} />
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
}
